import { useEffect, useState } from "react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { fetchProperties, updateProperty, createProperty } from "~/apis/properties"
import { PageSpinner } from "~/components/Spinner"

function PropertyEditor({ property, onSaved }) {
  const [ form, setForm ] = useState({
    locality: property.locality ?? "",
    color: property.color ?? "",
    checkin_time: property.checkin_time ?? "",
    checkout_time: property.checkout_time ?? "",
    cleaner_info: property.cleaner_info ?? "",
    checkin_info: property.checkin_info ?? "",
    residency_cleaner_note: property.residency_cleaner_note ?? "",
  })
  const [ saved, setSaved ] = useState(false)
  const [ copied, setCopied ] = useState(false)

  async function copyCheckinInfo() {
    await navigator.clipboard.writeText(form.checkin_info)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  const [ error, setError ] = useState(null)

  function set(key) {
    return (event) => setForm({ ...form, [key]: event.target.value })
  }

  async function save() {
    setError(null)
    try {
      const updated = await updateProperty(property.id, form)
      onSaved(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-border p-4">
      <h3 className="mb-3 font-semibold text-foreground">{property.name}</h3>
      {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Locality</Label>
          <Input value={form.locality} onChange={set("locality")} placeholder="e.g. Belgrave, VIC" className="mt-1" />
        </div>
        <div>
          <Label>Colour</Label>
          <Input type="color" value={form.color || "#000000"} onChange={set("color")} className="mt-1 h-9 p-1" />
        </div>
        <div />
        <div>
          <Label>Check-in time</Label>
          <Input type="time" value={form.checkin_time} onChange={set("checkin_time")} className="mt-1" />
        </div>
        <div>
          <Label>Check-out time</Label>
          <Input type="time" value={form.checkout_time} onChange={set("checkout_time")} className="mt-1" />
        </div>
        <div className="col-span-2">
          <Label>Cleaner info</Label>
          <Textarea value={form.cleaner_info} onChange={set("cleaner_info")} rows={2} className="mt-1" placeholder="Key location, alarm code, bin day…" />
          <p className="mt-1 text-xs text-muted-foreground">Shown to the cleaner on every clean in their portal.</p>
        </div>
        <div className="col-span-2">
          <Label>Residency note for the cleaner</Label>
          <Input value={form.residency_cleaner_note} onChange={set("residency_cleaner_note")} className="mt-1" placeholder="e.g. Residency stay — no linen to put out." />
          <p className="mt-1 text-xs text-muted-foreground">Added to the cleaner's notes for any clean around a residency at this property. Leave blank if residencies are cleaned like any other stay.</p>
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between">
            <Label>Check-in info for guests</Label>
            {form.checkin_info && (
              <Button type="button" variant="ghost" size="sm" onClick={copyCheckinInfo}>{copied ? "Copied ✓" : "Copy"}</Button>
            )}
          </div>
          <Textarea value={form.checkin_info} onChange={set("checkin_info")} rows={6} className="mt-1" placeholder="Directions, door code, wifi, house notes… everything a guest needs three days out." />
          <p className="mt-1 text-xs text-muted-foreground">Pasted into Riparide three days before check-in — the Today panel and inbox remind you. Airbnb guests get theirs from Airbnb.</p>
        </div>
      </div>

      <Button size="sm" className="mt-3" onClick={save}>
        {saved ? "Saved ✓" : "Save"}
      </Button>
    </div>
  )
}

function NewProperty({ onCreated }) {
  const [ name, setName ] = useState("")
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  async function submit(event) {
    event.preventDefault()
    if (!name.trim()) return

    setBusy(true)
    setError(null)
    try {
      const property = await createProperty({ name })
      onCreated(property)
      setName("")
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mb-4 flex items-start gap-2">
      <div className="flex-1">
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="New property name…" />
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
      <Button type="submit" disabled={busy}>Add property</Button>
    </form>
  )
}

export default function PropertyEditors() {
  const [ properties, setProperties ] = useState(null)
  const [ loadError, setLoadError ] = useState(null)

  useEffect(() => {
    fetchProperties().then(setProperties).catch((err) => setLoadError(err.message))
  }, [])

  function handleSaved(updated) {
    setProperties((current) => current.map((property) => (property.id === updated.id ? updated : property)))
  }

  function handleCreated(property) {
    setProperties((current) => [ ...current, property ])
  }

  if (loadError) return <p className="text-sm text-destructive">Couldn't load: {loadError}</p>
  if (!properties) return <PageSpinner />

  return (
    <div>
      <NewProperty onCreated={handleCreated} />
      {properties.map((property) => (
        <PropertyEditor key={property.id} property={property} onSaved={handleSaved} />
      ))}
    </div>
  )
}
